import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { paths } from "Constant/paths";
import { BRAND_NAME, CURRENT_YEAR } from "Constant/constants";
import useAuth from "Context/useAuth";

export default function Home() {
  const { toggleContactSalesModal } = useAuth();
  const { t } = useTranslation();
  const [showForFaculty, setShowForFaculty] = useState(false);

  // SEO Setup
  const baseUrl = process.env.REACT_APP_DEV_URL;
  const pageTitle = t("common.seo.home.pageTitle", {
    brandName: BRAND_NAME,
  });
  const canonicalUrl = `${baseUrl}/`;
  const ogImageUrl = `${baseUrl}/assets/images/home/applicants-banner.png`;

  // Function to handle the modal toggle
  const toggleShowForFaculty = () => {
    setShowForFaculty(!showForFaculty);
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={t("common.seo.home.metaDescription", {
            brandName: BRAND_NAME,
          })}
        />

        {/* Canonical */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={t("common.seo.home.ogTitle", {
            brandName: BRAND_NAME,
          })}
        />
        <meta
          property="og:description"
          content={t("common.seo.home.ogDescription", {
            brandName: BRAND_NAME,
          })}
        />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={t("common.seo.home.twitterTitle", {
            brandName: BRAND_NAME,
          })}
        />
        <meta
          name="twitter:description"
          content={t("common.seo.home.twitterDescription", {
            brandName: BRAND_NAME,
          })}
        />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>
      <section className="position-absolute start-0 end-0 z-5 pt-lg-20 pt-10">
        <div className="container-xl">
          <div className="row justify-content-center align-items-center">
            <div className="col-lg-6 col-md-12 col-sm-12 col-12 mb-5 mb-lg-0 order-lg-last text-center">
              {/* Bootstrap Switch Button */}
              <div
                className={`d-flex justify-content-center align-items-center column-gap-4 intro-witch ${
                  showForFaculty ? "active-switch" : ""
                }`}
              >
                <span className="font-semibold">For Faculty & Staff</span>
                <div className="form-check form-switch mb-0 min-h-unset rote-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="switchToggle"
                    checked={showForFaculty}
                    onChange={toggleShowForFaculty}
                    aria-label="Toggle between Faculty and Applicants"
                  />
                </div>
                <span className="font-semibold">For Applicants</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Content Changes Based on Switch State */}
      <main className="">
        <div className={`for-applicants-ui ${showForFaculty ? "active" : ""}`}>
          <section className="position-relative bg-primary-900 text-white">
            <div className="position-absolute top-0 start-0 bottom-0 end-0 text-center intro-pattern opacity-50">
              <div className="blur-2 bg-indigo-700 opacity-50"></div>
              <div className="blur-3 bg-primary-500 opacity-50"></div>
              <div className="blur-4 bg-primary-700 opacity-50"></div>
            </div>
            <div className="pt-20 position-relative z-1 transform">
              <div className="container-xl d-flex flex-column row-gap-5 pt-lg-16 pt-5">
                <div className="row justify-content-center">
                  <div className="col-xxl-9 col-xl-9 col-lg-12 col-md-12 col-sm-12 col-12">
                    <div className="text-center">
                      <h2 className="text-xxl-12xl text-xl-10xl text-lg-8xl text-md-5xl text-2xl font-bold mb-5">
                        Own Your Future: Streamlined College Admissions & Beyond
                      </h2>
                      <p className="text-xl font-medium mb-8">
                        Get real-time application updates, find Jobs, and join
                        campus events—all in one place.
                      </p>

                      <Link
                        to={paths.registration}
                        className="px-8 py-3 btn btn-light text-xl-lg font-medium  d-inline-flex align-items-center gap-1"
                      >
                        Join PlutoEdu for Free
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-column align-items-center position-relative">
                  <img
                    src="assets/images/home/applicants-banner.png"
                    alt="PlutoEdu Applicants Banner"
                    className="img-fluid"
                    width="1187"
                    height="436"
                  />
                  <div className="blur-5 bg-purple-500"></div>
                </div>
              </div>
            </div>
          </section>
          <section className="py-lg-20 py-10 position-relative bg-white z-1">
            <div className="container-xl d-flex flex-column row-gap-4">
              <div className="row justify-content-center align-items-center gx-xl-5">
                <div className="col-xl-7 col-lg-10 col-md-12 col-sm-12 col-12 text-center">
                  <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-xl font-bold">
                    Track Your Application Status in Real Time No More
                    Guesswork!
                  </h2>
                  <p>
                    See every update as it happens. Accept or reject offers
                    directly, request recommendations, and stay ahead of every
                    deadline from your personal dashboard.
                  </p>
                </div>
              </div>

              <div className="row justify-content-center align-items-center gx-xl-5 gy-4">
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="d-flex align-items-center gap-3">
                    <div className="w-20 h-20 flex-auto rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="23"
                        height="28"
                        viewBox="0 0 23 28"
                        fill="none"
                      >
                        <path
                          d="M3.81816 22.9093V11.4547C3.81816 9.42943 4.6227 7.4871 6.0548 6.055C7.4869 4.6229 9.42924 3.81836 11.4545 3.81836C13.4798 3.81836 15.4222 4.6229 16.8543 6.055C18.2863 7.4871 19.0909 9.42943 19.0909 11.4547V22.9093M3.81816 22.9093H19.0909M3.81816 22.9093H1.27271M19.0909 22.9093H21.6363M10.1818 26.7275H12.7273"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11.4546 3.81791C12.1575 3.81791 12.7273 3.2481 12.7273 2.54519C12.7273 1.84228 12.1575 1.27246 11.4546 1.27246C10.7517 1.27246 10.1819 1.84228 10.1819 2.54519C10.1819 3.2481 10.7517 3.81791 11.4546 3.81791Z"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xl-xl text-lg font-bold">
                        Real-Time Updates
                      </h4>
                      <p>Instant notifications on every status change.</p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="d-flex align-items-center gap-3">
                    <div className="w-20 h-20 flex-auto rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                      >
                        <path
                          d="M18.3974 13.3711C18.3974 13.9485 18.2837 14.5204 18.0627 15.0539C17.8417 15.5874 17.5178 16.0722 17.1094 16.4805C16.7011 16.8889 16.2163 17.2128 15.6828 17.4338C15.1493 17.6548 14.5775 17.7685 14 17.7685C13.4225 17.7685 12.8507 17.6548 12.3171 17.4338C11.7836 17.2128 11.2989 16.8889 10.8905 16.4805C10.4822 16.0722 10.1583 15.5874 9.93727 15.0539C9.71628 14.5204 9.60254 13.9485 9.60254 13.3711C9.60254 12.2048 10.0658 11.0863 10.8905 10.2616C11.7152 9.43693 12.8337 8.97363 14 8.97363C15.1662 8.97363 16.2848 9.43693 17.1094 10.2616C17.9341 11.0863 18.3974 12.2048 18.3974 13.3711Z"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M25.3076 16.0099C25.7046 15.8993 26.1234 15.8436 26.564 15.8428V10.9001C22.9744 10.9001 20.6413 7.00144 22.458 3.90691L18.1058 1.43555C16.2665 4.56903 11.7358 4.56903 9.89646 1.43555L5.54425 3.90691C7.36102 7.00144 5.02536 10.9001 1.43579 10.9001V15.8428C5.02536 15.8428 7.35851 19.7414 5.54174 22.836L9.89395 25.3073C10.3113 24.5891 10.9106 23.9936 11.6314 23.5809C12.3523 23.1682 13.1692 22.9529 13.9999 22.9566"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22.1665 17.7686L22.4906 18.6443C22.9153 19.7926 23.1276 20.3668 23.546 20.7852C23.9657 21.2048 24.5398 21.4172 25.6882 21.8418L26.5639 22.166L25.6882 22.4901C24.5398 22.9148 23.9657 23.1271 23.5473 23.5455C23.1276 23.9652 22.9153 24.5393 22.4906 25.6877L22.1665 26.5634L21.8423 25.6877C21.4177 24.5393 21.2053 23.9652 20.7869 23.5468C20.3673 23.1271 19.7931 22.9148 18.6448 22.4901L17.769 22.166L18.6448 21.8418C19.7931 21.4172 20.3673 21.2048 20.7857 20.7865C21.2053 20.3668 21.4177 19.7926 21.8423 18.6443L22.1665 17.7686Z"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xl-xl text-lg font-bold">
                        Detailed Timeline
                      </h4>
                      <p>View each application milestone at a glance.</p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="d-flex align-items-center gap-3">
                    <div className="w-20 h-20 flex-auto rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="28"
                        viewBox="0 0 24 28"
                        fill="none"
                      >
                        <path
                          d="M20.6411 11.0094C20.6411 14.2509 19.0454 16.7449 16.6181 18.387C16.0527 18.769 15.77 18.96 15.6305 19.1786C15.4923 19.3947 15.4433 19.6811 15.3491 20.2516L15.2737 20.6951C15.1078 21.6977 15.0237 22.199 14.6719 22.4968C14.3201 22.7945 13.8125 22.7945 12.796 22.7945H9.51431C8.49787 22.7945 7.99028 22.7945 7.63849 22.4968C7.28669 22.199 7.20377 21.6977 7.03541 20.6951L6.96254 20.2516C6.86705 19.6824 6.82056 19.3972 6.68236 19.1811C6.54415 18.965 6.2602 18.7715 5.69356 18.3845C3.29256 16.7424 1.79492 14.2497 1.79492 11.0094C1.79492 5.72368 6.01395 1.43555 11.218 1.43555C11.8511 1.43488 12.4825 1.49887 13.1026 1.62653"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13.7307 22.7945V24.0509C13.7307 25.2357 13.7307 25.8275 13.3626 26.1956C12.9945 26.5638 12.4027 26.5638 11.2179 26.5638C10.0331 26.5638 9.44133 26.5638 9.07321 26.1956C8.70508 25.8275 8.70508 25.2357 8.70508 24.0509V22.7945M17.4999 1.43555L17.8241 2.31126C18.2488 3.45962 18.4611 4.0338 18.8795 4.45219C19.2991 4.87183 19.8733 5.08416 21.0217 5.50883L21.8974 5.83298L21.0217 6.15714C19.8733 6.5818 19.2991 6.79414 18.8807 7.21252C18.4611 7.63216 18.2488 8.20634 17.8241 9.3547L17.4999 10.2304L17.1758 9.3547C16.7511 8.20634 16.5388 7.63216 16.1204 7.21378C15.7008 6.79414 15.1266 6.5818 13.9782 6.15714L13.1025 5.83298L13.9782 5.50883C15.1266 5.08416 15.7008 4.87183 16.1192 4.45344C16.5388 4.0338 16.7511 3.45962 17.1758 2.31126L17.4999 1.43555Z"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xl-xl text-lg font-bold">
                        Offer Management
                      </h4>
                      <p>
                        Accept, defer, or request more info—right from your
                        dashboard.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden">
                <video
                  width="100%"
                  height=""
                  autoPlay
                  muted
                  playsInline
                  loop
                  controls
                  preload="auto"
                  className="w-full h-auto"
                >
                  <source
                    src="assets/images/home/student_dashboard_video.mp4"
                    type="video/mp4"
                  />
                  {/* <source src="movie.ogg" type="video/ogg" /> */}
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="text-center">
                <Link
                  to={paths.registration}
                  className="px-8 py-3 btn btn-primary text-xl-lg d-inline-flex align-items-center gap-1 font-medium"
                >
                  Join Pluto Edu
                </Link>
              </div>
            </div>
          </section>
          <section className="pb-20">
            <div className="container-xl">
              <div className="row align-items-center gx-xl-5 gy-4">
                <div className="col-lg-5 col-md-6 col-sm-12 col-12 order-md-last">
                  <img
                    src="assets/images/home/land-internship-project.png"
                    alt="Land Internships & Projects"
                    className="img-fluid"
                    width="529"
                    height="422"
                  />
                </div>
                <div className="col-lg-7 col-md-6 col-sm-12 col-12 order-md-first">
                  <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-xl font-bold">
                    Land Internships & Projects — Boost Your Experience While
                    Studying
                  </h2>
                  <p>
                    Looking for the best internship websites? Read our full
                    guide:{" "}
                    <a
                      href="https://blog.plutoedu.com/2025/12/08/best-internship-websites-for-students-in-2025-complete-guide/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Best Internship Websites for Students in {CURRENT_YEAR}
                    </a>
                  </p>

                  <ul className="list-unstyle m-0 p-0 d-flex flex-column row-gap-3">
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="23"
                          height="15"
                          viewBox="0 0 23 15"
                          fill="none"
                        >
                          <path
                            d="M21.25 1.25H1.25M15 7.5H1.25M7.5 13.75H1.25"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        Browse a curated list of part-time roles and campus
                        projects.
                      </p>
                    </li>
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="18"
                          viewBox="0 0 16 18"
                          fill="none"
                        >
                          <path
                            d="M9.5288 3.97148C9.687 4.12949 9.90146 4.21824 10.1251 4.21824C10.3486 4.21824 10.5631 4.12949 10.7213 3.97148L11.5313 3.16148V14.6252C11.5313 14.849 11.6202 15.0636 11.7784 15.2219C11.9367 15.3801 12.1513 15.469 12.3751 15.469C12.5988 15.469 12.8134 15.3801 12.9717 15.2219C13.1299 15.0636 13.2188 14.849 13.2188 14.6252V3.16148L14.0288 3.97148C14.106 4.05438 14.1992 4.12087 14.3027 4.16699C14.4062 4.2131 14.5179 4.2379 14.6312 4.2399C14.7445 4.2419 14.857 4.22106 14.9621 4.17862C15.0672 4.13619 15.1626 4.07302 15.2427 3.9929C15.3228 3.91278 15.386 3.81734 15.4284 3.71228C15.4709 3.60722 15.4917 3.49469 15.4897 3.3814C15.4877 3.26811 15.4629 3.15638 15.4168 3.05288C15.3707 2.94938 15.3042 2.85623 15.2213 2.77898L12.9713 0.528984C12.8131 0.370977 12.5986 0.282227 12.3751 0.282227C12.1515 0.282227 11.937 0.370977 11.7788 0.528984L9.5288 2.77898C9.37079 2.93719 9.28204 3.15164 9.28204 3.37523C9.28204 3.59883 9.37079 3.81328 9.5288 3.97148ZM4.2188 14.839L5.0288 14.029C5.10605 13.9461 5.1992 13.8796 5.3027 13.8335C5.40619 13.7874 5.51792 13.7626 5.63121 13.7606C5.7445 13.7586 5.85704 13.7794 5.9621 13.8218C6.06716 13.8643 6.1626 13.9274 6.24272 14.0076C6.32284 14.0877 6.386 14.1831 6.42844 14.2882C6.47087 14.3933 6.49172 14.5058 6.48972 14.6191C6.48772 14.7324 6.46292 14.8441 6.4168 14.9476C6.37069 15.0511 6.3042 15.1442 6.2213 15.2215L3.9713 17.4715C3.8131 17.6295 3.59865 17.7182 3.37505 17.7182C3.15146 17.7182 2.937 17.6295 2.7788 17.4715L0.5288 15.2215C0.445903 15.1442 0.379413 15.0511 0.333297 14.9476C0.287181 14.8441 0.262384 14.7324 0.260385 14.6191C0.258386 14.5058 0.279226 14.3933 0.321662 14.2882C0.364099 14.1831 0.427261 14.0877 0.507382 14.0076C0.587503 13.9274 0.682941 13.8643 0.788003 13.8218C0.893065 13.7794 1.0056 13.7586 1.11889 13.7606C1.23218 13.7626 1.34391 13.7874 1.44741 13.8335C1.55091 13.8796 1.64406 13.9461 1.7213 14.029L2.5313 14.839V3.37523C2.5313 3.15146 2.6202 2.93685 2.77843 2.77861C2.93666 2.62038 3.15127 2.53148 3.37505 2.53148C3.59883 2.53148 3.81344 2.62038 3.97167 2.77861C4.12991 2.93685 4.2188 3.15146 4.2188 3.37523V14.839Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        Sort by location, industry, or time commitment.
                      </p>
                    </li>
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                        >
                          <path
                            d="M17.3571 13.4997V16.0712C17.3571 16.4121 17.2217 16.7392 16.9805 16.9803C16.7394 17.2214 16.4124 17.3569 16.0714 17.3569H13.5M13.5 0.642578H16.0714C16.4124 0.642578 16.7394 0.778037 16.9805 1.01916C17.2217 1.26027 17.3571 1.5873 17.3571 1.92829V4.49972M0.642822 4.49972V1.92829C0.642822 1.5873 0.778281 1.26027 1.0194 1.01916C1.26052 0.778037 1.58754 0.642578 1.92854 0.642578H4.49997M4.49997 17.3569H1.92854C1.58754 17.3569 1.26052 17.2214 1.0194 16.9803C0.778281 16.7392 0.642822 16.4121 0.642822 16.0712V13.4997M8.99997 8.35686C9.68195 8.35686 10.336 8.08595 10.8182 7.60371C11.3005 7.12147 11.5714 6.46742 11.5714 5.78544C11.5714 5.10345 11.3005 4.4494 10.8182 3.96716C10.336 3.48492 9.68195 3.21401 8.99997 3.21401C8.31798 3.21401 7.66393 3.48492 7.18169 3.96716C6.69945 4.4494 6.42854 5.10345 6.42854 5.78544C6.42854 6.46742 6.69945 7.12147 7.18169 7.60371C7.66393 8.08595 8.31798 8.35686 8.99997 8.35686ZM13.8895 14.1426C13.5572 13.107 12.9047 12.2036 12.0259 11.5627C11.1472 10.9218 10.0876 10.5764 8.99997 10.5764C7.91233 10.5764 6.85278 10.9218 5.97403 11.5627C5.09528 12.2036 4.44271 13.107 4.11039 14.1426H13.8895Z"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        Apply with your saved profile in just a few clicks.
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
          <section className="pb-20">
            <div className="container-xl d-flex flex-column row-gap-4">
              <div className="row justify-content-center align-items-center gx-xl-5">
                <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 text-center">
                  <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-xl font-bold">
                    Level Up Your Campus Life—Discover, Create, and Join Events
                  </h2>
                  <p>
                    Explore campus calendars, host events, sell used books, or
                    even livestream your study sessions. Connect with classmates
                    and make the most of university life.
                  </p>
                </div>
              </div>

              <div className="row justify-content-center">
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="h-100 p-4 rounded-xl border border-grey-200 d-flex flex-column row-gap-3">
                    <div className="rounded-md overflow-hidden">
                      <img
                        src="assets/images/home/academic-calendar.jpg"
                        alt="Academic Calendar & Upcoming Events"
                        className="w-100"
                        width="345"
                        height="220"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-grey-800">
                        Academic Calendar & Upcoming Events
                      </h3>
                      <p>
                        Stay on top of campus happenings by creating or joining
                        academic events
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="h-100 p-4 rounded-xl border border-grey-200 d-flex flex-column row-gap-3">
                    <div className="rounded-md overflow-hidden">
                      <img
                        src="assets/images/home/microblogging-social-feed.jpg"
                        alt="Microblogging & Social Feed"
                        className="w-100"
                        width="345"
                        height="220"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-grey-800">
                        Microblogging & Social Feed
                      </h3>
                      <p>Share updates, photos, or tips with your peers.</p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="h-100 p-4 rounded-xl border border-grey-200 d-flex flex-column row-gap-3">
                    <div className="rounded-md overflow-hidden">
                      <img
                        src="assets/images/home/bridge-campus-gaps.jpg"
                        alt="Bridge Campus Gaps with Livestream & Groups"
                        className="w-100"
                        width="345"
                        height="220"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-grey-800">
                        Bridge Campus Gaps with Livestream & Groups
                      </h3>
                      <p>
                        Bridge Campus Gaps with Virtual Hangouts, Collaborative
                        Live Stream..
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <Link
                  to={paths.registration}
                  className="px-8 py-3 btn btn-primary text-xl-lg d-inline-flex align-items-center gap-1 font-medium"
                >
                  Join Pluto Edu
                </Link>
              </div>
            </div>
          </section>
          <section className="pb-20">
            <div className="container-xl">
              <div className="row align-items-center gx-xl-5">
                <div className="col-lg-5 col-md-6 col-sm-12 col-12">
                  <img
                    src="assets/images/home/about-your-personal-info.png"
                    alt="Land Internships & Projects"
                    className="img-fluid"
                    width="642"
                    height="429"
                  />
                </div>
                <div className="col-lg-7 col-md-6 col-sm-12 col-12">
                  <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-xl font-bold">
                    Worried About Your Personal Info? Enjoy Peace of Mind with
                    Our Secure, GDPR-Compliant Platform.
                  </h2>
                  <p>
                    We encrypt your data, perform regular security audits, and
                    follow strict GDPR protocols. Rest easy knowing your
                    applicants’ information is protected from unauthorized
                    access.
                  </p>

                  <ul className="list-unstyle m-0 p-0 d-flex flex-column row-gap-3">
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-grey-100 bg-primary-50 d-flex justify-content-center align-items-center text-grey-100">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="19"
                          viewBox="0 0 22 19"
                          fill="none"
                        >
                          <path
                            d="M11.0666 18.9983C7.84381 18.9983 4.62105 18.9983 1.3983 18.9983C1.24087 18.9983 1.08141 18.9983 0.923304 18.9823C0.116486 18.9085 -0.20787 18.3758 0.136164 17.6555C0.434735 17.0336 0.760448 16.4277 1.07666 15.8125C2.56951 12.9158 4.06237 10.0196 5.55522 7.12382C5.60136 7.03537 5.64954 6.94691 5.70315 6.86244C6.07907 6.27185 6.68979 6.18871 7.17089 6.69817C7.51018 7.05466 7.79721 7.45504 8.09985 7.84145C9.63794 9.81144 11.176 11.7823 12.7141 13.7541C13.3859 14.612 14.2321 14.6586 15.0213 13.8871C15.4128 13.504 15.7955 13.1109 16.1918 12.7312C16.9063 12.0461 17.7172 12.0874 18.3008 12.8861C19.472 14.489 20.614 16.1131 21.7615 17.732C21.8512 17.859 21.9199 17.999 21.9651 18.147C22.0763 18.5188 21.9189 18.818 21.5342 18.9171C21.2929 18.9738 21.0453 18.9999 20.7972 18.995C17.5555 19.0003 14.3119 19.0014 11.0666 18.9983Z"
                            fill="#6B7280"
                          />
                          <path
                            d="M18.294 3.126C18.2932 3.74427 18.1051 4.34838 17.7535 4.86174C17.402 5.37511 16.9028 5.7746 16.3193 6.00958C15.7359 6.24456 15.0943 6.30444 14.4761 6.18163C13.8579 6.05882 13.2908 5.75885 12.8467 5.31976C12.4026 4.88066 12.1015 4.32221 11.9816 3.7152C11.8618 3.10819 11.9285 2.47996 12.1733 1.91016C12.4182 1.34036 12.8301 0.854648 13.3569 0.514598C13.8838 0.174548 14.5017 -0.00452273 15.1325 8.68032e-05C15.9725 0.0105488 16.7746 0.3441 17.366 0.928835C17.9574 1.51357 18.2907 2.30266 18.294 3.126Z"
                            fill="#6B7280"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        Your data is stored with top-tier encryption.
                      </p>
                    </li>
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-grey-100 bg-primary-50 d-flex justify-content-center align-items-center text-grey-100">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="19"
                          viewBox="0 0 22 19"
                          fill="none"
                        >
                          <path
                            d="M11.0666 18.9983C7.84381 18.9983 4.62105 18.9983 1.3983 18.9983C1.24087 18.9983 1.08141 18.9983 0.923304 18.9823C0.116486 18.9085 -0.20787 18.3758 0.136164 17.6555C0.434735 17.0336 0.760448 16.4277 1.07666 15.8125C2.56951 12.9158 4.06237 10.0196 5.55522 7.12382C5.60136 7.03537 5.64954 6.94691 5.70315 6.86244C6.07907 6.27185 6.68979 6.18871 7.17089 6.69817C7.51018 7.05466 7.79721 7.45504 8.09985 7.84145C9.63794 9.81144 11.176 11.7823 12.7141 13.7541C13.3859 14.612 14.2321 14.6586 15.0213 13.8871C15.4128 13.504 15.7955 13.1109 16.1918 12.7312C16.9063 12.0461 17.7172 12.0874 18.3008 12.8861C19.472 14.489 20.614 16.1131 21.7615 17.732C21.8512 17.859 21.9199 17.999 21.9651 18.147C22.0763 18.5188 21.9189 18.818 21.5342 18.9171C21.2929 18.9738 21.0453 18.9999 20.7972 18.995C17.5555 19.0003 14.3119 19.0014 11.0666 18.9983Z"
                            fill="#6B7280"
                          />
                          <path
                            d="M18.294 3.126C18.2932 3.74427 18.1051 4.34838 17.7535 4.86174C17.402 5.37511 16.9028 5.7746 16.3193 6.00958C15.7359 6.24456 15.0943 6.30444 14.4761 6.18163C13.8579 6.05882 13.2908 5.75885 12.8467 5.31976C12.4026 4.88066 12.1015 4.32221 11.9816 3.7152C11.8618 3.10819 11.9285 2.47996 12.1733 1.91016C12.4182 1.34036 12.8301 0.854648 13.3569 0.514598C13.8838 0.174548 14.5017 -0.00452273 15.1325 8.68032e-05C15.9725 0.0105488 16.7746 0.3441 17.366 0.928835C17.9574 1.51357 18.2907 2.30266 18.294 3.126Z"
                            fill="#6B7280"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        We meet global data protection requirements.
                      </p>
                    </li>
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-grey-100 bg-primary-50 d-flex justify-content-center align-items-center text-grey-100">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="19"
                          viewBox="0 0 22 19"
                          fill="none"
                        >
                          <path
                            d="M11.0666 18.9983C7.84381 18.9983 4.62105 18.9983 1.3983 18.9983C1.24087 18.9983 1.08141 18.9983 0.923304 18.9823C0.116486 18.9085 -0.20787 18.3758 0.136164 17.6555C0.434735 17.0336 0.760448 16.4277 1.07666 15.8125C2.56951 12.9158 4.06237 10.0196 5.55522 7.12382C5.60136 7.03537 5.64954 6.94691 5.70315 6.86244C6.07907 6.27185 6.68979 6.18871 7.17089 6.69817C7.51018 7.05466 7.79721 7.45504 8.09985 7.84145C9.63794 9.81144 11.176 11.7823 12.7141 13.7541C13.3859 14.612 14.2321 14.6586 15.0213 13.8871C15.4128 13.504 15.7955 13.1109 16.1918 12.7312C16.9063 12.0461 17.7172 12.0874 18.3008 12.8861C19.472 14.489 20.614 16.1131 21.7615 17.732C21.8512 17.859 21.9199 17.999 21.9651 18.147C22.0763 18.5188 21.9189 18.818 21.5342 18.9171C21.2929 18.9738 21.0453 18.9999 20.7972 18.995C17.5555 19.0003 14.3119 19.0014 11.0666 18.9983Z"
                            fill="#6B7280"
                          />
                          <path
                            d="M18.294 3.126C18.2932 3.74427 18.1051 4.34838 17.7535 4.86174C17.402 5.37511 16.9028 5.7746 16.3193 6.00958C15.7359 6.24456 15.0943 6.30444 14.4761 6.18163C13.8579 6.05882 13.2908 5.75885 12.8467 5.31976C12.4026 4.88066 12.1015 4.32221 11.9816 3.7152C11.8618 3.10819 11.9285 2.47996 12.1733 1.91016C12.4182 1.34036 12.8301 0.854648 13.3569 0.514598C13.8838 0.174548 14.5017 -0.00452273 15.1325 8.68032e-05C15.9725 0.0105488 16.7746 0.3441 17.366 0.928835C17.9574 1.51357 18.2907 2.30266 18.294 3.126Z"
                            fill="#6B7280"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        We keep an eye on vulnerabilities so you don’t have to.
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div
          className={`for-faculty-ui position-relative ${
            !showForFaculty ? "active" : ""
          }`}
        >
          <section className="position-relative">
            <div className="position-absolute top-0 start-0 bottom-0 end-0 text-center intro-pattern opacity-50">
              <div className="blur-2 bg-indigo-700 opacity-50"></div>
              <div className="blur-3 bg-primary-500 opacity-50"></div>
              <div className="blur-4 bg-primary-700 opacity-50"></div>
            </div>
            <div className="pt-20 position-relative z-1 transform">
              <div className="container-xl d-flex flex-column row-gap-5 pt-lg-16 pt-5">
                <div className="row justify-content-center">
                  <div className="col-xxl-9 col-xl-9 col-lg-12 col-md-12 col-sm-12 col-12">
                    <div className="text-center">
                      <h2 className="text-xxl-12xl text-xl-10xl text-lg-8xl text-md-5xl text-2xl font-bold text-primary mb-5">
                        Experience the Next Level of University Recruitment
                      </h2>
                      <p className="text-xl font-medium mb-8">
                        Handle every applicant detail —Fast, Organized, and
                        Hassle-free.
                      </p>

                      <button
                        className="px-8 py-3 btn btn-primary text-xl-lg font-medium d-inline-flex align-items-center gap-1"
                        onClick={toggleContactSalesModal}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 32 32"
                          fill="none"
                        >
                          <path
                            d="M16 14C15.1595 14.0003 14.3472 14.303 13.7115 14.8529C13.0758 15.4027 12.6592 16.163 12.5378 16.9947C12.4165 17.8264 12.5985 18.674 13.0506 19.3825C13.5027 20.0911 14.1946 20.6133 15 20.8538V23C15 23.2652 15.1054 23.5196 15.2929 23.7071C15.4804 23.8946 15.7348 24 16 24C16.2652 24 16.5196 23.8946 16.7071 23.7071C16.8946 23.5196 17 23.2652 17 23V20.8538C17.8054 20.6133 18.4973 20.0911 18.9494 19.3825C19.4015 18.674 19.5835 17.8264 19.4622 16.9947C19.3408 16.163 18.9242 15.4027 18.2885 14.8529C17.6528 14.303 16.8405 14.0003 16 14ZM16 19C15.7033 19 15.4133 18.912 15.1666 18.7472C14.92 18.5824 14.7277 18.3481 14.6142 18.074C14.5006 17.7999 14.4709 17.4983 14.5288 17.2074C14.5867 16.9164 14.7296 16.6491 14.9393 16.4393C15.1491 16.2296 15.4164 16.0867 15.7074 16.0288C15.9983 15.9709 16.2999 16.0007 16.574 16.1142C16.8481 16.2277 17.0824 16.42 17.2472 16.6666C17.412 16.9133 17.5 17.2033 17.5 17.5C17.5 17.8978 17.342 18.2794 17.0607 18.5607C16.7794 18.842 16.3978 19 16 19ZM26 10H22V7C22 5.4087 21.3679 3.88258 20.2426 2.75736C19.1174 1.63214 17.5913 1 16 1C14.4087 1 12.8826 1.63214 11.7574 2.75736C10.6321 3.88258 10 5.4087 10 7V10H6C5.46957 10 4.96086 10.2107 4.58579 10.5858C4.21071 10.9609 4 11.4696 4 12V26C4 26.5304 4.21071 27.0391 4.58579 27.4142C4.96086 27.7893 5.46957 28 6 28H26C26.5304 28 27.0391 27.7893 27.4142 27.4142C27.7893 27.0391 28 26.5304 28 26V12C28 11.4696 27.7893 10.9609 27.4142 10.5858C27.0391 10.2107 26.5304 10 26 10ZM12 7C12 5.93913 12.4214 4.92172 13.1716 4.17157C13.9217 3.42143 14.9391 3 16 3C17.0609 3 18.0783 3.42143 18.8284 4.17157C19.5786 4.92172 20 5.93913 20 7V10H12V7ZM26 26H6V12H26V26Z"
                            fill="currentColor"
                          />
                        </svg>
                        Unlock 6 Months Free Trial
                      </button>
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-column align-items-center">
                  <img
                    src="assets/images/home/facility-banner.png"
                    alt="All-In-One Toolkit for Admissions"
                    className="img-fluid"
                    width="1187"
                    height="426"
                  />
                  <div className="position-relative">
                    <div className="blur-1 bg-purple-500"></div>
                    <div className="toolkit-box rounded-2xl bg-white d-flex flex-column row-gap-3 position-relative">
                      <h3 className="text-lg-xl text-lg font-bold text-grey-800 text-center">
                        All-in-One Toolkit for Admissions
                      </h3>
                      <div className="toolkits-list">
                        <ul className="list-unstyled d-inline-flex flex-wrap justify-content-center gap-3">
                          <li className="p-2 rounded-xl border border-grey-200 bg-white d-flex align-items-center gap-2">
                            <div className="w-12 h-12 flex-auto rounded-lg bg-primary-500 d-flex justify-content-center align-items-center text d-flex justify-content-center align-items-center text-white">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <path
                                  d="M26.8263 24.8275C27.3861 24.2683 27.7675 23.5555 27.9222 22.7795C28.0769 22.0035 27.9979 21.199 27.6953 20.4678C27.3927 19.7367 26.88 19.1117 26.2221 18.672C25.5642 18.2322 24.7907 17.9975 23.9994 17.9975C23.2081 17.9975 22.4345 18.2322 21.7766 18.672C21.1187 19.1117 20.6061 19.7367 20.3034 20.4678C20.0008 21.199 19.9219 22.0035 20.0766 22.7795C20.2313 23.5555 20.6127 24.2683 21.1725 24.8275C20.1209 25.4984 19.3581 26.5381 19.0338 27.7425C18.9655 27.9988 19.0018 28.2717 19.1347 28.5012C19.2676 28.7307 19.4862 28.898 19.7425 28.9663C19.8265 28.9884 19.9131 28.9998 20 29C20.2205 28.9999 20.4348 28.927 20.6096 28.7924C20.7844 28.6579 20.9097 28.4694 20.9662 28.2563C21.32 26.9275 22.5675 26 24 26C25.4325 26 26.68 26.9275 27.0338 28.2563C27.0654 28.3854 27.1225 28.5069 27.2017 28.6137C27.2809 28.7205 27.3806 28.8104 27.4949 28.8783C27.6093 28.9461 27.736 28.9904 27.8677 29.0087C27.9994 29.0269 28.1334 29.0187 28.2619 28.9846C28.3904 28.9504 28.5108 28.891 28.616 28.8097C28.7213 28.7285 28.8093 28.6271 28.8749 28.5114C28.9404 28.3958 28.9823 28.2682 28.998 28.1362C29.0137 28.0041 29.0029 27.8703 28.9662 27.7425C28.6416 26.5379 27.8783 25.4982 26.8263 24.8275ZM24 20C24.3956 20 24.7822 20.1173 25.1111 20.3371C25.44 20.5568 25.6964 20.8692 25.8478 21.2346C25.9991 21.6001 26.0387 22.0022 25.9616 22.3902C25.8844 22.7782 25.6939 23.1345 25.4142 23.4142C25.1345 23.6939 24.7781 23.8844 24.3902 23.9616C24.0022 24.0388 23.6001 23.9991 23.2346 23.8478C22.8692 23.6964 22.5568 23.44 22.3371 23.1112C22.1173 22.7823 22 22.3956 22 22C22 21.4696 22.2107 20.9609 22.5858 20.5858C22.9609 20.2107 23.4696 20 24 20ZM27 9.00001H16.4137L13 5.58626C12.815 5.39973 12.5947 5.25185 12.352 5.15121C12.1093 5.05057 11.849 4.99917 11.5863 5.00001H5C4.46957 5.00001 3.96086 5.21072 3.58579 5.5858C3.21071 5.96087 3 6.46958 3 7.00001V25.0763C3.00033 25.5862 3.20295 26.0751 3.56338 26.4357C3.92381 26.7964 4.41261 26.9993 4.9225 27H15.07C15.3352 27 15.5896 26.8947 15.7771 26.7071C15.9646 26.5196 16.07 26.2652 16.07 26C16.07 25.7348 15.9646 25.4804 15.7771 25.2929C15.5896 25.1054 15.3352 25 15.07 25H5V11H27V15C27 15.2652 27.1054 15.5196 27.2929 15.7071C27.4804 15.8947 27.7348 16 28 16C28.2652 16 28.5196 15.8947 28.7071 15.7071C28.8946 15.5196 29 15.2652 29 15V11C29 10.4696 28.7893 9.96087 28.4142 9.5858C28.0391 9.21072 27.5304 9.00001 27 9.00001ZM11.5863 7.00001L13.5863 9.00001H5V7.00001H11.5863Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>
                            <div className="pr-4">
                              <span className="font-medium text-grey-800">
                                Streamlined applicants data for swift review
                              </span>
                            </div>
                          </li>
                          <li className="p-2 rounded-xl border border-grey-200 bg-white d-flex align-items-center gap-2">
                            <div className="w-12 h-12 flex-auto rounded-lg bg-lime-500 d-flex justify-content-center align-items-center text-white">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <path
                                  d="M26 4H23V3C23 2.73478 22.8946 2.48043 22.7071 2.29289C22.5196 2.10536 22.2652 2 22 2C21.7348 2 21.4804 2.10536 21.2929 2.29289C21.1054 2.48043 21 2.73478 21 3V4H11V3C11 2.73478 10.8946 2.48043 10.7071 2.29289C10.5196 2.10536 10.2652 2 10 2C9.73478 2 9.48043 2.10536 9.29289 2.29289C9.10536 2.48043 9 2.73478 9 3V4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V26C4 26.5304 4.21071 27.0391 4.58579 27.4142C4.96086 27.7893 5.46957 28 6 28H26C26.5304 28 27.0391 27.7893 27.4142 27.4142C27.7893 27.0391 28 26.5304 28 26V6C28 5.46957 27.7893 4.96086 27.4142 4.58579C27.0391 4.21071 26.5304 4 26 4ZM9 6V7C9 7.26522 9.10536 7.51957 9.29289 7.70711C9.48043 7.89464 9.73478 8 10 8C10.2652 8 10.5196 7.89464 10.7071 7.70711C10.8946 7.51957 11 7.26522 11 7V6H21V7C21 7.26522 21.1054 7.51957 21.2929 7.70711C21.4804 7.89464 21.7348 8 22 8C22.2652 8 22.5196 7.89464 22.7071 7.70711C22.8946 7.51957 23 7.26522 23 7V6H26V10H6V6H9ZM26 26H6V12H26V26ZM21.2075 15.2925C21.3005 15.3854 21.3742 15.4957 21.4246 15.6171C21.4749 15.7385 21.5008 15.8686 21.5008 16C21.5008 16.1314 21.4749 16.2615 21.4246 16.3829C21.3742 16.5043 21.3005 16.6146 21.2075 16.7075L15.2075 22.7075C15.1146 22.8005 15.0043 22.8742 14.8829 22.9246C14.7615 22.9749 14.6314 23.0008 14.5 23.0008C14.3686 23.0008 14.2385 22.9749 14.1171 22.9246C13.9957 22.8742 13.8854 22.8005 13.7925 22.7075L10.7925 19.7075C10.6049 19.5199 10.4994 19.2654 10.4994 19C10.4994 18.7346 10.6049 18.4801 10.7925 18.2925C10.9801 18.1049 11.2346 17.9994 11.5 17.9994C11.7654 17.9994 12.0199 18.1049 12.2075 18.2925L14.5 20.5863L19.7925 15.2925C19.8854 15.1995 19.9957 15.1258 20.1171 15.0754C20.2385 15.0251 20.3686 14.9992 20.5 14.9992C20.6314 14.9992 20.7615 15.0251 20.8829 15.0754C21.0043 15.1258 21.1146 15.1995 21.2075 15.2925Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>
                            <div className="pr-4">
                              <span className="font-medium text-grey-800">
                                Built-In To-Do List & Reminders
                              </span>
                            </div>
                          </li>
                          <li className="p-2 rounded-xl border border-grey-200 bg-white d-flex align-items-center gap-2">
                            <div className="w-12 h-12 flex-auto rounded-lg bg-violet-500 d-flex justify-content-center align-items-center text-white">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <path
                                  d="M28 6H4C3.73478 6 3.48043 6.10536 3.29289 6.29289C3.10536 6.48043 3 6.73478 3 7V24C3 24.5304 3.21071 25.0391 3.58579 25.4142C3.96086 25.7893 4.46957 26 5 26H27C27.5304 26 28.0391 25.7893 28.4142 25.4142C28.7893 25.0391 29 24.5304 29 24V7C29 6.73478 28.8946 6.48043 28.7071 6.29289C28.5196 6.10536 28.2652 6 28 6ZM16 16.6437L6.57125 8H25.4287L16 16.6437ZM12.3387 16L5 22.7262V9.27375L12.3387 16ZM13.8188 17.3563L15.3188 18.7375C15.5032 18.9069 15.7446 19.0008 15.995 19.0008C16.2454 19.0008 16.4868 18.9069 16.6712 18.7375L18.1712 17.3563L25.4212 24H6.57125L13.8188 17.3563ZM19.6612 16L27 9.2725V22.7275L19.6612 16Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>
                            <div className="pr-4">
                              <span className="font-medium text-grey-800">
                                AI-Powered Email & Bulk Offer Letters
                              </span>
                            </div>
                          </li>
                          <li className="p-2 rounded-xl border border-grey-200 bg-white d-flex align-items-center gap-2">
                            <div className="w-12 h-12 flex-auto rounded-lg bg-yellow-500 d-flex justify-content-center align-items-center text-white">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <path
                                  d="M27.5338 19.8176C27.3912 19.7276 27.2282 19.6752 27.0599 19.6651C26.8916 19.6551 26.7235 19.6877 26.5713 19.7601C26.1902 19.9403 25.7698 20.0212 25.3491 19.9953C24.9284 19.9695 24.521 19.8377 24.1649 19.6122C23.8088 19.3867 23.5155 19.0748 23.3122 18.7055C23.109 18.3362 23.0024 17.9216 23.0024 17.5001C23.0024 17.0785 23.109 16.6639 23.3122 16.2946C23.5155 15.9253 23.8088 15.6134 24.1649 15.3879C24.521 15.1624 24.9284 15.0306 25.3491 15.0048C25.7698 14.9789 26.1902 15.0598 26.5713 15.2401C26.7237 15.3124 26.892 15.345 27.0604 15.3348C27.2288 15.3246 27.392 15.272 27.5346 15.1818C27.6772 15.0915 27.7946 14.9667 27.876 14.8189C27.9574 14.6711 28.0001 14.505 28 14.3363V9.00005C28 8.46962 27.7893 7.96091 27.4142 7.58584C27.0391 7.21077 26.5304 7.00005 26 7.00005H21.4725C21.4911 6.83404 21.5003 6.66711 21.5 6.50005C21.499 5.88529 21.3725 5.27721 21.1283 4.71301C20.8842 4.14882 20.5274 3.64038 20.08 3.2188C19.4603 2.63677 18.6883 2.24222 17.8536 2.08084C17.0188 1.91945 16.1554 1.99785 15.3634 2.30694C14.5714 2.61604 13.8831 3.14318 13.3783 3.82732C12.8736 4.51145 12.5729 5.32459 12.5112 6.17255C12.492 6.44835 12.4979 6.72532 12.5287 7.00005H8C7.46957 7.00005 6.96086 7.21077 6.58579 7.58584C6.21071 7.96091 6 8.46962 6 9.00005V13.0276C5.83399 13.0089 5.66705 12.9998 5.5 13.0001C4.88542 13.001 4.2775 13.1275 3.7135 13.3717C3.1495 13.6158 2.64129 13.9726 2.22 14.4201C1.79569 14.8683 1.46896 15.3996 1.26047 15.9805C1.05199 16.5615 0.966274 17.1793 1.00875 17.7951C1.08041 18.8793 1.54109 19.9012 2.30609 20.6728C3.07108 21.4445 4.08894 21.914 5.1725 21.9951C5.44829 22.0143 5.72526 22.0084 6 21.9776V26.0001C6 26.5305 6.21071 27.0392 6.58579 27.4143C6.96086 27.7893 7.46957 28.0001 8 28.0001H26C26.5304 28.0001 27.0391 27.7893 27.4142 27.4143C27.7893 27.0392 28 26.5305 28 26.0001V20.6638C28.0001 20.4948 27.9574 20.3286 27.8759 20.1806C27.7943 20.0326 27.6766 19.9077 27.5338 19.8176ZM26 26.0001H8V20.6638C8.00005 20.4951 7.95741 20.329 7.87603 20.1812C7.79465 20.0334 7.67719 19.9086 7.53458 19.8184C7.39197 19.7281 7.22885 19.6755 7.06041 19.6653C6.89197 19.6551 6.7237 19.6877 6.57125 19.7601C6.19021 19.9403 5.76978 20.0212 5.34907 19.9953C4.92836 19.9695 4.52101 19.8377 4.1649 19.6122C3.80879 19.3867 3.51548 19.0748 3.31224 18.7055C3.10899 18.3362 3.00242 17.9216 3.00242 17.5001C3.00242 17.0785 3.10899 16.6639 3.31224 16.2946C3.51548 15.9253 3.80879 15.6134 4.1649 15.3879C4.52101 15.1624 4.92836 15.0306 5.34907 15.0048C5.76978 14.9789 6.19021 15.0598 6.57125 15.2401C6.7237 15.3124 6.89197 15.345 7.06041 15.3348C7.22885 15.3246 7.39197 15.272 7.53458 15.1818C7.67719 15.0915 7.79465 14.9667 7.87603 14.8189C7.95741 14.6711 8.00005 14.505 8 14.3363V9.00005H13.8362C14.005 9.00011 14.171 8.95746 14.3188 8.87608C14.4667 8.7947 14.5915 8.67724 14.6817 8.53463C14.7719 8.39202 14.8246 8.2289 14.8347 8.06047C14.8449 7.89203 14.8123 7.72375 14.74 7.5713C14.5598 7.19026 14.4789 6.76984 14.5047 6.34912C14.5306 5.92841 14.6624 5.52106 14.8879 5.16495C15.1134 4.80884 15.4253 4.51553 15.7946 4.31229C16.1638 4.10905 16.5785 4.00247 17 4.00247C17.4215 4.00247 17.8362 4.10905 18.2054 4.31229C18.5747 4.51553 18.8866 4.80884 19.1121 5.16495C19.3376 5.52106 19.4694 5.92841 19.4953 6.34912C19.5211 6.76984 19.4402 7.19026 19.26 7.5713C19.1877 7.72375 19.1551 7.89203 19.1652 8.06047C19.1754 8.2289 19.2281 8.39202 19.3183 8.53463C19.4085 8.67724 19.5333 8.7947 19.6812 8.87608C19.829 8.95746 19.995 9.00011 20.1637 9.00005H26V13.0288C25.7253 12.9973 25.4483 12.9915 25.1725 13.0113C24.0086 13.0937 22.9223 13.625 22.1426 14.493C21.3629 15.361 20.9509 16.4979 20.9933 17.6639C21.0358 18.8299 21.5295 19.9338 22.3703 20.7428C23.2111 21.5518 24.3332 22.0026 25.5 22.0001C25.6671 22.0003 25.834 21.9912 26 21.9726V26.0001Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>
                            <div className="pr-4">
                              <span className="font-medium text-grey-800">
                                Drag & Drop Form Builder
                              </span>
                            </div>
                          </li>
                          <li className="p-2 rounded-xl border border-grey-200 bg-white d-flex align-items-center gap-2">
                            <div className="w-12 h-12 flex-auto rounded-lg bg-red-500 d-flex justify-content-center align-items-center text-white">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <path
                                  d="M28.9737 23.4158C29.6789 21.9822 30.0304 20.4004 29.9987 18.8031C29.967 17.2057 29.553 15.6391 28.7914 14.2346C28.0298 12.8302 26.9427 11.6286 25.6213 10.7306C24.2998 9.83259 22.7824 9.26429 21.1962 9.07329C20.6701 7.84694 19.9039 6.73829 18.9427 5.81265C17.9815 4.88701 16.8448 4.16311 15.5995 3.68359C14.3543 3.20407 13.0256 2.97863 11.6918 3.02056C10.3581 3.0625 9.0462 3.37095 7.83349 3.92774C6.62079 4.48454 5.53179 5.27842 4.63065 6.26261C3.72952 7.2468 3.03448 8.40139 2.58648 9.65835C2.13847 10.9153 1.94658 12.2492 2.02209 13.5815C2.09761 14.9138 2.43901 16.2175 3.02617 17.4158L2.06867 20.7683C1.98323 21.0682 1.97949 21.3855 2.05783 21.6874C2.13617 21.9892 2.29376 22.2647 2.51427 22.4852C2.73479 22.7057 3.01023 22.8633 3.31208 22.9416C3.61394 23.02 3.93125 23.0162 4.23117 22.9308L7.58367 21.9733C8.59531 22.47 9.6835 22.7924 10.8024 22.927C11.3358 24.1767 12.1182 25.3045 13.1019 26.2417C14.0857 27.1789 15.2501 27.9057 16.524 28.378C17.798 28.8502 19.1549 29.0579 20.5118 28.9883C21.8687 28.9187 23.1972 28.5734 24.4162 27.9733L27.7687 28.9308C28.0686 29.0162 28.3859 29.02 28.6878 28.9416C28.9896 28.8633 29.2651 28.7057 29.4856 28.4852C29.7061 28.2647 29.8637 27.9892 29.942 27.6874C30.0204 27.3855 30.0166 27.0682 29.9312 26.7683L28.9737 23.4158ZM7.69117 19.9033C7.59823 19.9033 7.50572 19.9159 7.41617 19.9408L4.11492 20.8845L5.05867 17.5833C5.1294 17.3318 5.09889 17.0626 4.97367 16.8333C4.05419 15.1452 3.77379 13.1823 4.18387 11.3043C4.59394 9.4263 5.66702 7.75895 7.20646 6.60778C8.74591 5.45662 10.6486 4.89874 12.5659 5.03637C14.4833 5.174 16.2868 5.99793 17.646 7.35717C19.0053 8.71641 19.8292 10.5199 19.9668 12.4373C20.1045 14.3546 19.5466 16.2573 18.3954 17.7967C17.2443 19.3362 15.5769 20.4093 13.6989 20.8193C11.8209 21.2294 9.85801 20.949 8.16992 20.0295C8.02357 19.9477 7.85886 19.9043 7.69117 19.9033ZM26.9412 23.5833L27.8849 26.8845L24.5837 25.9408C24.3322 25.8701 24.063 25.9006 23.8337 26.0258C21.9903 27.0287 19.8269 27.2678 17.809 26.6918C15.791 26.1158 14.0799 24.7706 13.0437 22.9458C14.413 22.8032 15.7381 22.3791 16.9357 21.7002C18.1334 21.0213 19.1779 20.1022 20.0036 19.0006C20.8293 17.899 21.4184 16.6386 21.734 15.2985C22.0495 13.9584 22.0848 12.5676 21.8374 11.2133C23.0357 11.4957 24.1526 12.0508 25.1013 12.8355C26.0499 13.6202 26.8046 14.6132 27.3067 15.7372C27.8088 16.8613 28.0447 18.0861 27.9961 19.3162C27.9474 20.5463 27.6155 21.7487 27.0262 22.8295C26.9 23.0599 26.8694 23.3306 26.9412 23.5833Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>
                            <div className="pr-4">
                              <span className="font-medium text-grey-800">
                                Chat, share files, and collaborate instantly—all
                                in one platform
                              </span>
                            </div>
                          </li>
                          <li className="p-2 rounded-xl border border-grey-200 bg-white d-flex align-items-center gap-2">
                            <div className="w-12 h-12 flex-auto rounded-lg bg-teal-400 d-flex justify-content-center align-items-center text-white">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <path
                                  d="M4 8C4 7.73478 4.10536 7.48043 4.29289 7.29289C4.48043 7.10536 4.73478 7 5 7H27C27.2652 7 27.5196 7.10536 27.7071 7.29289C27.8946 7.48043 28 7.73478 28 8C28 8.26522 27.8946 8.51957 27.7071 8.70711C27.5196 8.89464 27.2652 9 27 9H5C4.73478 9 4.48043 8.89464 4.29289 8.70711C4.10536 8.51957 4 8.26522 4 8ZM5 17H14C14.2652 17 14.5196 16.8946 14.7071 16.7071C14.8946 16.5196 15 16.2652 15 16C15 15.7348 14.8946 15.4804 14.7071 15.2929C14.5196 15.1054 14.2652 15 14 15H5C4.73478 15 4.48043 15.1054 4.29289 15.2929C4.10536 15.4804 4 15.7348 4 16C4 16.2652 4.10536 16.5196 4.29289 16.7071C4.48043 16.8946 4.73478 17 5 17ZM16 23H5C4.73478 23 4.48043 23.1054 4.29289 23.2929C4.10536 23.4804 4 23.7348 4 24C4 24.2652 4.10536 24.5196 4.29289 24.7071C4.48043 24.8946 4.73478 25 5 25H16C16.2652 25 16.5196 24.8946 16.7071 24.7071C16.8946 24.5196 17 24.2652 17 24C17 23.7348 16.8946 23.4804 16.7071 23.2929C16.5196 23.1054 16.2652 23 16 23ZM29.7075 24.7075C29.6146 24.8005 29.5043 24.8742 29.3829 24.9246C29.2615 24.9749 29.1314 25.0008 29 25.0008C28.8686 25.0008 28.7385 24.9749 28.6171 24.9246C28.4957 24.8742 28.3854 24.8005 28.2925 24.7075L25.75 22.17C24.716 22.8522 23.4657 23.1262 22.2412 22.9391C21.0167 22.752 19.9052 22.117 19.1222 21.1572C18.3392 20.1973 17.9403 18.981 18.0029 17.7439C18.0655 16.5067 18.5851 15.3369 19.461 14.461C20.3369 13.5851 21.5067 13.0655 22.7439 13.0029C23.981 12.9403 25.1973 13.3392 26.1572 14.1222C27.117 14.9052 27.752 16.0167 27.9391 17.2412C28.1262 18.4657 27.8522 19.716 27.17 20.75L29.7075 23.2875C29.8012 23.3805 29.8756 23.4911 29.9264 23.6129C29.9772 23.7348 30.0033 23.8655 30.0033 23.9975C30.0033 24.1295 29.9772 24.2602 29.9264 24.3821C29.8756 24.5039 29.8012 24.6145 29.7075 24.7075ZM23 21C23.5933 21 24.1734 20.8241 24.6667 20.4944C25.1601 20.1648 25.5446 19.6962 25.7716 19.1481C25.9987 18.5999 26.0581 17.9967 25.9424 17.4147C25.8266 16.8328 25.5409 16.2982 25.1213 15.8787C24.7018 15.4591 24.1672 15.1734 23.5853 15.0576C23.0033 14.9419 22.4001 15.0013 21.8519 15.2284C21.3038 15.4554 20.8352 15.8399 20.5056 16.3333C20.1759 16.8266 20 17.4067 20 18C20 18.7956 20.3161 19.5587 20.8787 20.1213C21.4413 20.6839 22.2044 21 23 21Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>
                            <div className="pr-4">
                              <span className="font-medium text-grey-800">
                                Global Advanced Search & Filters
                              </span>
                            </div>
                          </li>
                          <li className="p-2 rounded-xl border border-grey-200 bg-white d-flex align-items-center gap-2">
                            <div className="w-12 h-12 flex-auto rounded-lg bg-pink-400 d-flex justify-content-center align-items-center text-white">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <path
                                  d="M14.6563 19.7401C15.9966 18.8479 17.0141 17.548 17.5584 16.0327C18.1028 14.5174 18.145 12.8671 17.6789 11.326C17.2129 9.78483 16.2632 8.43458 14.9703 7.4749C13.6775 6.51522 12.1102 5.99707 10.5001 5.99707C8.88997 5.99707 7.32261 6.51522 6.02978 7.4749C4.73694 8.43458 3.78726 9.78483 3.32118 11.326C2.85509 12.8671 2.89734 14.5174 3.44168 16.0327C3.98603 17.548 5.00356 18.8479 6.34381 19.7401C3.91943 20.6337 1.84894 22.2872 0.441312 24.4539C0.367332 24.5638 0.315945 24.6874 0.290139 24.8174C0.264332 24.9474 0.264621 25.0812 0.290989 25.2111C0.317356 25.3409 0.369276 25.4643 0.44373 25.5739C0.518185 25.6835 0.613688 25.7773 0.72469 25.8497C0.835692 25.9221 0.959977 25.9717 1.09032 25.9956C1.22067 26.0196 1.35447 26.0174 1.48396 25.9892C1.61344 25.9609 1.73603 25.9073 1.84458 25.8312C1.95314 25.7552 2.04551 25.6584 2.11631 25.5464C3.0243 24.1498 4.26676 23.0023 5.73086 22.2078C7.19496 21.4134 8.83432 20.9973 10.5001 20.9973C12.1658 20.9973 13.8052 21.4134 15.2693 22.2078C16.7334 23.0023 17.9758 24.1498 18.8838 25.5464C19.0305 25.7644 19.257 25.9159 19.5145 25.9681C19.772 26.0204 20.0397 25.9692 20.2598 25.8257C20.4799 25.6822 20.6346 25.4578 20.6906 25.2011C20.7465 24.9444 20.6992 24.676 20.5588 24.4539C19.1512 22.2872 17.0807 20.6337 14.6563 19.7401ZM5.00006 13.5001C5.00006 12.4123 5.32263 11.3489 5.92698 10.4445C6.53133 9.54001 7.39031 8.83506 8.3953 8.41878C9.4003 8.00249 10.5062 7.89358 11.5731 8.10579C12.64 8.31801 13.62 8.84184 14.3891 9.61103C15.1583 10.3802 15.6822 11.3602 15.8944 12.4271C16.1066 13.494 15.9977 14.5999 15.5814 15.6049C15.1651 16.6099 14.4602 17.4688 13.5557 18.0732C12.6512 18.6775 11.5879 19.0001 10.5001 19.0001C9.04188 18.9985 7.64389 18.4185 6.6128 17.3874C5.58171 16.3563 5.00172 14.9583 5.00006 13.5001ZM31.2676 25.8376C31.0454 25.9825 30.7749 26.0331 30.5154 25.9785C30.2559 25.9239 30.0287 25.7685 29.8838 25.5464C28.9769 24.149 27.7346 23.0009 26.2702 22.2068C24.8058 21.4127 23.1659 20.9979 21.5001 21.0001C21.2348 21.0001 20.9805 20.8948 20.793 20.7072C20.6054 20.5197 20.5001 20.2653 20.5001 20.0001C20.5001 19.7349 20.6054 19.4805 20.793 19.293C20.9805 19.1055 21.2348 19.0001 21.5001 19.0001C22.31 18.9993 23.1098 18.8197 23.8423 18.474C24.5748 18.1283 25.2219 17.6251 25.7373 17.0003C26.2528 16.3756 26.6239 15.6447 26.8242 14.8598C27.0244 14.075 27.0488 13.2557 26.8957 12.4603C26.7426 11.6649 26.4157 10.9132 25.9383 10.2589C25.461 9.60449 24.845 9.06362 24.1344 8.6749C23.4239 8.28619 22.6362 8.05921 21.8277 8.0102C21.0192 7.96118 20.2099 8.09134 19.4576 8.39136C19.3349 8.44439 19.2028 8.47229 19.0692 8.47342C18.9356 8.47455 18.8031 8.44889 18.6795 8.39794C18.556 8.34699 18.4439 8.2718 18.3499 8.1768C18.2559 8.0818 18.1819 7.96893 18.1323 7.84485C18.0827 7.72077 18.0584 7.588 18.0609 7.45439C18.0635 7.32077 18.0928 7.18903 18.1471 7.06693C18.2015 6.94483 18.2797 6.83486 18.3772 6.74352C18.4748 6.65218 18.5897 6.58131 18.7151 6.53511C20.4369 5.84843 22.352 5.82372 24.091 6.46575C25.83 7.10778 27.2696 8.37106 28.1321 10.0119C28.9946 11.6527 29.2189 13.5548 28.7617 15.3513C28.3045 17.1477 27.1982 18.7112 25.6563 19.7401C28.0807 20.6337 30.1512 22.2872 31.5588 24.4539C31.7037 24.676 31.7543 24.9466 31.6997 25.2061C31.6451 25.4655 31.4897 25.6927 31.2676 25.8376Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>
                            <div className="pr-4">
                              <span className="font-medium text-grey-800">
                                Multiple Roles & Permissions to keep workflows
                                efficient
                              </span>
                            </div>
                          </li>
                          <li className="p-2 rounded-xl border border-grey-200 bg-white d-flex align-items-center gap-2">
                            <div className="w-12 h-12 flex-auto rounded-lg bg-indigo-600 d-flex justify-content-center align-items-center text-white">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <path
                                  d="M16 14C15.1595 14.0003 14.3472 14.303 13.7115 14.8529C13.0758 15.4027 12.6592 16.163 12.5378 16.9947C12.4165 17.8264 12.5985 18.674 13.0506 19.3825C13.5027 20.0911 14.1946 20.6133 15 20.8538V23C15 23.2652 15.1054 23.5196 15.2929 23.7071C15.4804 23.8946 15.7348 24 16 24C16.2652 24 16.5196 23.8946 16.7071 23.7071C16.8946 23.5196 17 23.2652 17 23V20.8538C17.8054 20.6133 18.4973 20.0911 18.9494 19.3825C19.4015 18.674 19.5835 17.8264 19.4622 16.9947C19.3408 16.163 18.9242 15.4027 18.2885 14.8529C17.6528 14.303 16.8405 14.0003 16 14ZM16 19C15.7033 19 15.4133 18.912 15.1666 18.7472C14.92 18.5824 14.7277 18.3481 14.6142 18.074C14.5006 17.7999 14.4709 17.4983 14.5288 17.2074C14.5867 16.9164 14.7296 16.6491 14.9393 16.4393C15.1491 16.2296 15.4164 16.0867 15.7074 16.0288C15.9983 15.9709 16.2999 16.0007 16.574 16.1142C16.8481 16.2277 17.0824 16.42 17.2472 16.6666C17.412 16.9133 17.5 17.2033 17.5 17.5C17.5 17.8978 17.342 18.2794 17.0607 18.5607C16.7794 18.842 16.3978 19 16 19ZM26 10H22V7C22 5.4087 21.3679 3.88258 20.2426 2.75736C19.1174 1.63214 17.5913 1 16 1C14.4087 1 12.8826 1.63214 11.7574 2.75736C10.6321 3.88258 10 5.4087 10 7V10H6C5.46957 10 4.96086 10.2107 4.58579 10.5858C4.21071 10.9609 4 11.4696 4 12V26C4 26.5304 4.21071 27.0391 4.58579 27.4142C4.96086 27.7893 5.46957 28 6 28H26C26.5304 28 27.0391 27.7893 27.4142 27.4142C27.7893 27.0391 28 26.5304 28 26V12C28 11.4696 27.7893 10.9609 27.4142 10.5858C27.0391 10.2107 26.5304 10 26 10ZM12 7C12 5.93913 12.4214 4.92172 13.1716 4.17157C13.9217 3.42143 14.9391 3 16 3C17.0609 3 18.0783 3.42143 18.8284 4.17157C19.5786 4.92172 20 5.93913 20 7V10H12V7ZM26 26H6V12H26V26Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>
                            <div className="pr-4">
                              <span className="font-medium text-grey-800">
                                Protect sensitive info with encryption
                              </span>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="py-lg-20 py-10">
            <div className="container-xl">
              <div className="row align-items-center gx-xl-5 gy-4">
                <div className="col-lg-6 col-md-6 col-sm-12 col-12">
                  <img
                    src="assets/images/home/organize-evaluate-and-decide-all-in-one-view.png"
                    alt=""
                    className="img-fluid"
                    width="607"
                    height="433"
                  />
                </div>
                <div className="col-lg-6 col-md-6 col-sm-12 col-12">
                  <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-xl font-bold">
                    Organize, Evaluate, and Decide—All in One View
                  </h2>
                  <p>
                    With PlutoEdu's color-coded Applicant status, integrated
                    comments, and rating system, you can see applicant progress
                    at a glance—no more endless scrolling. Make faster decisions
                    with less effort.
                  </p>

                  <ul className="list-unstyle m-0 p-0 d-flex flex-column row-gap-4">
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="28"
                          height="28"
                          viewBox="0 0 28 28"
                          fill="none"
                        >
                          <path
                            d="M15.1666 3.50233C14.6183 3.50078 14.0349 3.5 13.4166 3.5C8.19225 3.5 5.57892 3.5 3.95609 5.12283C2.33325 6.74567 2.33325 9.35783 2.33325 14.5833C2.33325 19.8077 2.33325 22.421 3.95609 24.0438C5.57892 25.6667 8.19108 25.6667 13.4166 25.6667C18.6409 25.6667 21.2542 25.6667 22.8771 24.0438C24.4999 22.421 24.4999 19.8088 24.4999 14.5833C24.4999 13.965 24.4991 13.3817 24.4976 12.8333"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8.1665 20.4163C10.8872 17.5673 15.9167 17.4332 18.6665 20.4163M21.5832 2.33301L21.8842 3.14617C22.2785 4.21251 22.4757 4.74567 22.8642 5.13417C23.2538 5.52384 23.787 5.72101 24.8533 6.11534L25.6665 6.41634L24.8533 6.71734C23.787 7.11168 23.2538 7.30884 22.8653 7.69734C22.4757 8.08701 22.2785 8.62018 21.8842 9.68651L21.5832 10.4997L21.2822 9.68651C20.8878 8.62018 20.6907 8.08701 20.3022 7.69851C19.9125 7.30884 19.3793 7.11168 18.313 6.71734L17.4998 6.41634L18.313 6.11534C19.3793 5.72101 19.9125 5.52384 20.301 5.13534C20.6907 4.74567 20.8878 4.21251 21.2822 3.14617L21.5832 2.33301ZM16.3273 11.6663C16.3273 13.2763 15.0207 14.583 13.4072 14.583C13.0238 14.5836 12.644 14.5086 12.2896 14.3623C11.9352 14.216 11.6131 14.0013 11.3418 13.7304C11.0704 13.4595 10.8552 13.1378 10.7083 12.7836C10.5614 12.4294 10.4858 12.0498 10.4858 11.6663C10.4858 10.0563 11.7925 8.74968 13.4072 8.74968C13.7905 8.74922 14.1701 8.82432 14.5244 8.97069C14.8787 9.11706 15.2006 9.33183 15.4718 9.60271C15.743 9.87359 15.9582 10.1953 16.105 10.5494C16.2518 10.9035 16.3273 11.283 16.3273 11.6663Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        Color-Coded Statuses: Spot priority applicants
                        instantly.
                      </p>
                    </li>
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="28"
                          height="28"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M1.81812 6.90927V4.36381C1.81812 3.68872 2.0863 3.04127 2.56366 2.56391C3.04103 2.08654 3.68847 1.81836 4.36357 1.81836H6.90902M1.81812 17.0911V19.6365C1.81812 20.3116 2.0863 20.9591 2.56366 21.4364C3.04103 21.9138 3.68847 22.182 4.36357 22.182H6.90902M17.0908 1.81836H19.6363C20.3114 1.81836 20.9588 2.08654 21.4362 2.56391C21.9136 3.04127 22.1817 3.68872 22.1817 4.36381V6.90927M17.0908 22.182H19.6363C20.3114 22.182 20.9588 21.9138 21.4362 21.4364C21.9136 20.9591 22.1817 20.3116 22.1817 19.6365V17.0911M6.90902 17.0911C6.90902 16.416 7.1772 15.7685 7.65457 15.2912C8.13194 14.8138 8.77938 14.5456 9.45448 14.5456H14.5454C15.2205 14.5456 15.8679 14.8138 16.3453 15.2912C16.8227 15.7685 17.0908 16.416 17.0908 17.0911M9.45448 8.18199C9.45448 8.85709 9.72266 9.50454 10.2 9.9819C10.6774 10.4593 11.3248 10.7274 11.9999 10.7274C12.675 10.7274 13.3225 10.4593 13.7998 9.9819C14.2772 9.50454 14.5454 8.85709 14.5454 8.18199C14.5454 7.5069 14.2772 6.85945 13.7998 6.38209C13.3225 5.90472 12.675 5.63654 11.9999 5.63654C11.3248 5.63654 10.6774 5.90472 10.2 6.38209C9.72266 6.85945 9.45448 7.5069 9.45448 8.18199Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        Built-In Ratings: Quickly assess candidates within the
                        dashboard.
                      </p>
                    </li>
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="25"
                          height="20"
                          viewBox="0 0 25 20"
                          fill="none"
                        >
                          <path
                            d="M10.7 9.83789C9.77285 9.83789 8.86657 9.56298 8.09572 9.04791C7.32486 8.53284 6.72405 7.80075 6.36927 6.94422C6.01448 6.08769 5.92165 5.14519 6.10252 4.23591C6.28339 3.32662 6.72983 2.49139 7.38539 1.83583C8.04095 1.18027 8.87618 0.73383 9.78546 0.552962C10.6947 0.372093 11.6372 0.464922 12.4938 0.819707C13.3503 1.17449 14.0824 1.7753 14.5975 2.54616C15.1125 3.31701 15.3875 4.22329 15.3875 5.15039C15.3842 6.39258 14.8892 7.58295 14.0109 8.46131C13.1325 9.33967 11.9421 9.83459 10.7 9.83789ZM10.7 2.33789C10.1437 2.33789 9.59992 2.50284 9.13741 2.81188C8.6749 3.12093 8.31441 3.56018 8.10154 4.0741C7.88867 4.58801 7.83297 5.15351 7.94149 5.69908C8.05001 6.24466 8.31788 6.7458 8.71121 7.13913C9.10455 7.53247 9.60569 7.80033 10.1513 7.90885C10.6968 8.01737 11.2623 7.96168 11.7762 7.7488C12.2902 7.53593 12.7294 7.17545 13.0385 6.71293C13.3475 6.25042 13.5125 5.70665 13.5125 5.15039C13.5125 4.40447 13.2161 3.6891 12.6887 3.16166C12.1612 2.63421 11.4459 2.33789 10.7 2.33789ZM1.94995 18.5879C1.70131 18.5879 1.46285 18.4891 1.28704 18.3133C1.11122 18.1375 1.01245 17.899 1.01245 17.6504C1.01245 11.7129 7.79995 11.7129 10.7 11.7129C11.6 11.7129 12.4 11.7129 13.125 11.8004C13.3716 11.8196 13.6006 11.9355 13.7621 12.1228C13.9237 12.3101 14.0047 12.5536 13.9874 12.8004C13.9652 13.0481 13.8466 13.2771 13.6573 13.4384C13.468 13.5997 13.223 13.6803 12.9749 13.6629C12.2999 13.6629 11.55 13.5879 10.7 13.5879C4.22495 13.5879 2.88745 15.2129 2.88745 17.6504C2.88913 17.774 2.86603 17.8966 2.81951 18.0111C2.773 18.1256 2.704 18.2297 2.61661 18.3171C2.52922 18.4044 2.4252 18.4734 2.3107 18.52C2.19619 18.5665 2.07353 18.5896 1.94995 18.5879ZM13.3375 19.5379C13.0889 19.5377 12.8506 19.4388 12.6749 19.2629C12.5762 19.1688 12.5002 19.0536 12.4526 18.9258C12.4049 18.798 12.3869 18.6611 12.4 18.5254L12.6 16.1254C12.6154 15.9047 12.7084 15.6966 12.8624 15.5379L19.7624 8.63789C20.2385 8.19687 20.8635 7.95184 21.5124 7.95184C22.1614 7.95184 22.7864 8.19687 23.2625 8.63789C23.4972 8.87446 23.6822 9.15556 23.8067 9.46468C23.9312 9.7738 23.9927 10.1047 23.9874 10.4379C23.9923 10.745 23.9363 11.0501 23.8226 11.3354C23.7089 11.6208 23.5397 11.8808 23.3249 12.1004L16.4249 19.0004C16.2719 19.1573 16.0681 19.2547 15.85 19.2754L13.425 19.5004L13.3375 19.5379ZM14.4374 16.6129L14.3625 17.5629L15.3375 17.4754L22 10.8129C22.0776 10.718 22.1176 10.5979 22.1124 10.4754C22.1109 10.3016 22.0491 10.1337 21.9374 10.0004C21.8146 9.90907 21.6655 9.85976 21.5124 9.85976C21.3594 9.85976 21.2103 9.90907 21.0875 10.0004L14.4374 16.6129Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        Comment Threads: Collaborate with faculty directly.
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="pb-20">
            <div className="container-xl d-flex flex-column row-gap-4">
              <div className="row justify-content-center align-items-center gx-xl-5">
                <div className="col-xl-7 col-lg-10 col-md-12 col-sm-12 col-12 text-center">
                  <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-xl font-bold">
                    Create Any Form, for Any Admission Niche No Coding Required!
                  </h2>
                  <p>
                    Our drag-and-drop form builder adapts to every program—from
                    undergrad admissions to faculty recruitments. Just pick,
                    place, and publish.
                  </p>
                </div>
              </div>

              <div className="row justify-content-center align-items-center gx-xl-5 gy-4">
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="d-flex align-items-center gap-3">
                    <div className="w-20 h-20 flex-auto rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="27"
                        height="28"
                        viewBox="0 0 27 28"
                        fill="none"
                      >
                        <path
                          d="M13.2222 6.48163H26.4444M0 6.48163H5.50926M5.50926 6.48163C5.50926 7.65054 5.97361 8.77158 6.80016 9.59813C7.62671 10.4247 8.74775 10.889 9.91667 10.889C11.0856 10.889 12.2066 10.4247 13.0332 9.59813C13.8597 8.77158 14.3241 7.65054 14.3241 6.48163C14.3241 5.31271 13.8597 4.19167 13.0332 3.36512C12.2066 2.53857 11.0856 2.07422 9.91667 2.07422C8.74775 2.07422 7.62671 2.53857 6.80016 3.36512C5.97361 4.19167 5.50926 5.31271 5.50926 6.48163ZM0 21.9076H13.2222M20.9352 21.9076H26.4444M20.9352 21.9076C20.9352 23.0765 20.4708 24.1975 19.6443 25.0241C18.8177 25.8506 17.6967 26.315 16.5278 26.315C15.3589 26.315 14.2378 25.8506 13.4113 25.0241C12.5847 24.1975 12.1204 23.0765 12.1204 21.9076C12.1204 20.7386 12.5847 19.6176 13.4113 18.791C14.2378 17.9645 15.3589 17.5001 16.5278 17.5001C17.6967 17.5001 18.8177 17.9645 19.6443 18.791C20.4708 19.6176 20.9352 20.7386 20.9352 21.9076Z"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        />{" "}
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xl-xl text-lg font-bold">
                        Advanced Logic
                      </h4>
                      <p className="mb-0">
                        Show or hide fields based on responses.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="d-flex align-items-center gap-3">
                    <div className="w-20 h-20 flex-auto rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                      >
                        <path
                          d="M18.3974 13.3711C18.3974 13.9485 18.2837 14.5204 18.0627 15.0539C17.8417 15.5874 17.5178 16.0722 17.1094 16.4805C16.7011 16.8889 16.2163 17.2128 15.6828 17.4338C15.1493 17.6548 14.5775 17.7685 14 17.7685C13.4225 17.7685 12.8507 17.6548 12.3171 17.4338C11.7836 17.2128 11.2989 16.8889 10.8905 16.4805C10.4822 16.0722 10.1583 15.5874 9.93727 15.0539C9.71628 14.5204 9.60254 13.9485 9.60254 13.3711C9.60254 12.2048 10.0658 11.0863 10.8905 10.2616C11.7152 9.43693 12.8337 8.97363 14 8.97363C15.1662 8.97363 16.2848 9.43693 17.1094 10.2616C17.9341 11.0863 18.3974 12.2048 18.3974 13.3711Z"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M25.3076 16.0099C25.7046 15.8993 26.1234 15.8436 26.564 15.8428V10.9001C22.9744 10.9001 20.6413 7.00144 22.458 3.90691L18.1058 1.43555C16.2665 4.56903 11.7358 4.56903 9.89646 1.43555L5.54425 3.90691C7.36102 7.00144 5.02536 10.9001 1.43579 10.9001V15.8428C5.02536 15.8428 7.35851 19.7414 5.54174 22.836L9.89395 25.3073C10.3113 24.5891 10.9106 23.9936 11.6314 23.5809C12.3523 23.1682 13.1692 22.9529 13.9999 22.9566"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22.1665 17.7686L22.4906 18.6443C22.9153 19.7926 23.1276 20.3668 23.546 20.7852C23.9657 21.2048 24.5398 21.4172 25.6882 21.8418L26.5639 22.166L25.6882 22.4901C24.5398 22.9148 23.9657 23.1271 23.5473 23.5455C23.1276 23.9652 22.9153 24.5393 22.4906 25.6877L22.1665 26.5634L21.8423 25.6877C21.4177 24.5393 21.2053 23.9652 20.7869 23.5468C20.3673 23.1271 19.7931 22.9148 18.6448 22.4901L17.769 22.166L18.6448 21.8418C19.7931 21.4172 20.3673 21.2048 20.7857 20.7865C21.2053 20.3668 21.4177 19.7926 21.8423 18.6443L22.1665 17.7686Z"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xl-xl text-lg font-bold">
                        Seamless Integration
                      </h4>
                      <p className="mb-0">
                        Embed forms on your website or send direct links.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="d-flex align-items-center gap-3">
                    <div className="w-20 h-20 flex-auto rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        viewBox="0 0 26 26"
                        fill="none"
                      >
                        <path
                          d="M1.44434 2.88878C1.44434 2.50569 1.59652 2.13829 1.8674 1.8674C2.13829 1.59652 2.50569 1.44434 2.88878 1.44434H23.111C23.4941 1.44434 23.8615 1.59652 24.1324 1.8674C24.4033 2.13829 24.5555 2.50569 24.5555 2.88878V5.77767C24.5555 6.16076 24.4033 6.52816 24.1324 6.79905C23.8615 7.06993 23.4941 7.22211 23.111 7.22211H2.88878C2.50569 7.22211 2.13829 7.06993 1.8674 6.79905C1.59652 6.52816 1.44434 6.16076 1.44434 5.77767V2.88878ZM1.44434 14.4443C1.44434 14.0612 1.59652 13.6938 1.8674 13.423C2.13829 13.1521 2.50569 12.9999 2.88878 12.9999H11.5554C11.9385 12.9999 12.3059 13.1521 12.5768 13.423C12.8477 13.6938 12.9999 14.0612 12.9999 14.4443V23.111C12.9999 23.4941 12.8477 23.8615 12.5768 24.1324C12.3059 24.4033 11.9385 24.5555 11.5554 24.5555H2.88878C2.50569 24.5555 2.13829 24.4033 1.8674 24.1324C1.59652 23.8615 1.44434 23.4941 1.44434 23.111V14.4443ZM18.7777 14.4443C18.7777 14.0612 18.9299 13.6938 19.2007 13.423C19.4716 13.1521 19.839 12.9999 20.2221 12.9999H23.111C23.4941 12.9999 23.8615 13.1521 24.1324 13.423C24.4033 13.6938 24.5555 14.0612 24.5555 14.4443V23.111C24.5555 23.4941 24.4033 23.8615 24.1324 24.1324C23.8615 24.4033 23.4941 24.5555 23.111 24.5555H20.2221C19.839 24.5555 19.4716 24.4033 19.2007 24.1324C18.9299 23.8615 18.7777 23.4941 18.7777 23.111V14.4443Z"
                          stroke="#1E40AF"
                          strokeWidth="2.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xl-xl text-lg font-bold">
                        Global Form Template
                      </h4>
                      <p className="mb-0">
                        Access pre-built templates tailored to admission use
                        cases across countries.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden">
                <video
                  width="100%"
                  height=""
                  autoPlay
                  muted
                  playsInline
                  loop
                  controls
                  preload="auto"
                  className="w-full h-auto"
                >
                  <source
                    src="assets/images/home/admin_dashboard_video.mp4"
                    type="video/mp4"
                  />
                  {/* <source src="movie.ogg" type="video/ogg" /> */}
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="text-center">
                <button
                  className="px-8 py-3 btn btn-primary text-xl-lg d-inline-flex align-items-center gap-1 font-medium"
                  onClick={toggleContactSalesModal}
                >
                  Start Your 6-Month Free Trial
                </button>
              </div>
            </div>
          </section>

          <section className="pb-20">
            <div className="container-xl">
              <div className="row align-items-center gx-xl-5 gy-4">
                <div className="col-lg-5 col-md-6 col-sm-12 col-12 order-md-last">
                  <img
                    src="assets/images/home/about-data-privacy.png"
                    alt="About Data Privacy"
                    className="img-fluid"
                    width="500"
                    height="500"
                  />
                </div>
                <div className="col-lg-7 col-md-6 col-sm-12 col-12 order-md-first">
                  <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-xl font-bold">
                    Worried About Data Privacy? We're GDPR-Compliant and Secure
                  </h2>
                  <p>
                    We encrypt your data, perform regular security audits, and
                    follow strict GDPR protocols. Rest easy knowing your
                    applicants’ information is protected from unauthorized
                    access.
                  </p>

                  <ul className="list-unstyle m-0 p-0 d-flex flex-column row-gap-4">
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="22"
                          viewBox="0 0 20 22"
                          fill="none"
                        >
                          <path
                            d="M10.4762 14.6667C10.4762 15.8295 9.54381 16.7619 8.38095 16.7619C7.82526 16.7619 7.29233 16.5412 6.8994 16.1482C6.50646 15.7553 6.28571 15.2224 6.28571 14.6667C6.28571 13.5038 7.2181 12.5714 8.38095 12.5714C8.93664 12.5714 9.46958 12.7922 9.86251 13.1851C10.2554 13.578 10.4762 14.111 10.4762 14.6667ZM9.52286 19.9048C9.64857 20.659 9.91048 21.361 10.2771 22H2.09524C1.53955 22 1.00661 21.7793 0.613681 21.3863C0.220748 20.9934 0 20.4605 0 19.9048V9.42857C0 8.26571 0.932381 7.33333 2.09524 7.33333H3.14286V5.2381C3.14286 2.34667 5.48952 0 8.38095 0C11.2724 0 13.619 2.34667 13.619 5.2381V7.33333H14.6667C15.2224 7.33333 15.7553 7.55408 16.1482 7.94702C16.5412 8.33995 16.7619 8.87288 16.7619 9.42857V12.6657C16.4162 12.6133 16.0705 12.5714 15.7143 12.5714C15.3581 12.5714 15.0124 12.6133 14.6667 12.6657V9.42857H2.09524V19.9048H9.52286ZM5.2381 7.33333H11.5238V5.2381C11.5238 3.49905 10.12 2.09524 8.38095 2.09524C6.64191 2.09524 5.2381 3.49905 5.2381 5.2381V7.33333ZM18.1657 15.5467L14.4048 19.3076L12.739 17.6419L11.5238 18.8571L14.4048 22L19.381 17.0238L18.1657 15.5467Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        End-to-End Encryption: Keep sensitive details safe.
                      </p>
                    </li>
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="24"
                          viewBox="0 0 18 24"
                          fill="none"
                        >
                          <path
                            d="M17.318 3.98203L9.3539 1.26797C9.25781 1.23516 9.12891 1.21875 9 1.21875C8.87109 1.21875 8.74219 1.23516 8.64609 1.26797L0.682031 3.98203C0.4875 4.04766 0.328125 4.27266 0.328125 4.47891V15.7852C0.328125 15.9914 0.461719 16.2633 0.623438 16.3922L8.70234 22.6875C8.78437 22.7508 8.88984 22.7836 8.99766 22.7836C9.10547 22.7836 9.21328 22.7508 9.29297 22.6875L17.3719 16.3922C17.5336 16.2656 17.6672 15.9937 17.6672 15.7852V4.47891C17.6719 4.27266 17.5125 4.05 17.318 3.98203ZM15.9844 15.3352L9 20.7773L2.01562 15.3352V5.31328L9 2.93203L15.9844 5.31328V15.3352ZM6.44297 12.3937L4.62656 14.2102C4.59167 14.2454 4.57209 14.293 4.57209 14.3426C4.57209 14.3922 4.59167 14.4398 4.62656 14.475L5.42344 15.2719C5.49609 15.3445 5.61562 15.3445 5.68828 15.2719L7.50469 13.4555C8.81016 14.2781 10.5539 14.1211 11.6906 12.9844C13.0102 11.6648 13.0102 9.52734 11.6906 8.20781C10.3711 6.88828 8.23359 6.88828 6.91406 8.20781C5.77734 9.34453 5.62031 11.0906 6.44297 12.3937ZM7.97578 9.26953C8.70937 8.53594 9.89766 8.53594 10.6289 9.26953C11.3625 10.0031 11.3625 11.1914 10.6289 11.9227C9.89531 12.6562 8.70703 12.6562 7.97578 11.9227C7.24453 11.1891 7.24219 10.0031 7.97578 9.26953Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        Regular Security Audits: Prevent breaches before they
                        happen.
                      </p>
                    </li>
                    <li className="d-flex align-items-center gap-3">
                      <div className="flex-auto w-12 h-12 rounded-circle border border-primary-900 bg-primary-50 d-flex justify-content-center align-items-center text-primary-900">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="18"
                          viewBox="0 0 24 18"
                          fill="none"
                        >
                          <path
                            d="M8.90471 16.9412H5.82353C4.23529 16.9412 2.83765 16.4118 1.70471 15.2788C0.571765 14.1671 0 12.8012 0 11.2024C0 9.82588 0.412941 8.59765 1.23882 7.51765C2.04438 6.46198 3.20122 5.72956 4.5 5.45294C4.94471 3.83294 5.82353 2.52 7.14706 1.51412C8.47059 0.529412 9.97412 0 11.6471 0C13.7118 0 15.4694 0.72 16.8988 2.16C18.3388 3.58941 19.0588 5.34706 19.0588 7.41177C20.2765 7.54941 21.2929 8.07882 22.0871 9C22.7647 9.77294 23.1459 10.6518 23.2518 11.6471H21.1341C21.0282 11.1176 20.7953 10.6835 20.4035 10.3024C19.8953 9.78353 19.2706 9.52941 18.5294 9.52941H16.9412V7.41177C16.9412 5.95059 16.4118 4.70118 15.3953 3.66353C14.3682 2.64706 13.1188 2.11765 11.6471 2.11765C10.1859 2.11765 8.93647 2.64706 7.89882 3.66353C6.88235 4.70118 6.35294 5.95059 6.35294 7.41177H5.82353C4.79647 7.41177 3.92824 7.77176 3.20824 8.50235C2.47765 9.22235 2.11765 10.0906 2.11765 11.1176C2.11765 12.1447 2.47765 13.0235 3.20824 13.7647C3.92824 14.4741 4.79647 14.8235 5.82353 14.8235H8.47059C8.47059 15.5859 8.62941 16.2953 8.90471 16.9412ZM23.2941 13.7647V15.8824H21.1765V18H19.0588V15.8824H16.7294C16.3059 17.1529 15.1412 18 13.7647 18C11.9647 18 10.5882 16.6235 10.5882 14.8235C10.5882 13.0235 11.9647 11.6471 13.7647 11.6471C15.1412 11.6471 16.3059 12.4941 16.7294 13.7647H23.2941ZM14.8235 14.8235C14.8235 14.2941 14.4 13.7647 13.7647 13.7647C13.1294 13.7647 12.7059 14.2941 12.7059 14.8235C12.7059 15.3529 13.1294 15.8824 13.7647 15.8824C14.4 15.8824 14.8235 15.3529 14.8235 14.8235Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                      <p className="mb-0">
                        GDPR Compliance: Meets European data protection
                        standards.
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="pb-20">
            <div className="container-xl d-flex flex-column row-gap-4">
              <div className="row justify-content-center align-items-center gx-xl-5">
                <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 text-center">
                  <h2 className="text-xl-8xl text-md-6xl text-sm-4xl text-xl font-bold">
                    Beyond Admissions—Tools to Simplify Your Entire Workflow
                  </h2>
                  <p>
                    Not Just Admission Software—Everything You Need for a
                    Seamless Process
                  </p>
                </div>
              </div>

              <div className="row justify-content-center gy-4">
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="h-100 p-4 rounded-xl border border-grey-200 d-flex flex-column row-gap-3">
                    <div className="rounded-md overflow-hidden">
                      <img
                        src="assets/images/home/to-do-list-track.jpg"
                        alt="Stay on Track with To-Do Lists"
                        className="w-100"
                        width="345"
                        height="220"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-grey-800">
                        Stay on Track with To-Do Lists
                      </h3>
                      <p>
                        Create personal tasks, set reminders, and never miss a
                        deadline.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="h-100 p-4 rounded-xl border border-grey-200 d-flex flex-column row-gap-3">
                    <div className="rounded-md overflow-hidden">
                      <img
                        src="assets/images/home/collaborate-instantly.jpg"
                        alt="Collaborate Instantly with Pluto Connect"
                        className="w-100"
                        width="345"
                        height="220"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-grey-800">
                        Collaborate Instantly with Pluto Connect
                      </h3>
                      <p>
                        Real-time group chats, file sharing, and quick
                        updates—all within the platform.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12">
                  <div className="h-100 p-4 rounded-xl border border-grey-200 d-flex flex-column row-gap-3">
                    <div className="rounded-md overflow-hidden">
                      <img
                        src="assets/images/home/control-access.jpg"
                        alt="Control Access with Multiple Roles & Permissions"
                        className="w-100"
                        width="345"
                        height="220"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-grey-800">
                        Control Access with Multiple Roles & Permissions
                      </h3>
                      <p>
                        Assign specific privileges to faculty, staff, or
                        reviewers for secure, collaboration.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <button
                  className="px-8 py-3 btn btn-primary text-xl-lg d-inline-flex align-items-center gap-1 font-medium"
                  onClick={toggleContactSalesModal}
                >
                  Start Your 6-Month Free Trial
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
